/**
 * One store per grid (the Overview, or one Analytics dashboard). Holds the layout, customize mode, the
 * picked-up widget (keyboard), 50-step history, and autosave with version checks. When someone else saved
 * in between, the server answers 409 with their layout and the store merges per widget (mergeLayouts).
 * (From the handoff starter; generic over the item type so the Overview's typed widgets flow through, plus
 * `previewBox` for the top/left corner handles.)
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import { recipes } from "../../motion";
import {
  addItem, announce, compact, keyboardMove, keyboardResize, mergeLayouts, moveItem, placeItem, removeItem, resizeItem, sameLayout,
  type Dir, type LayoutItem,
} from "./layoutEngine";
import { WIDGETS, widgetType } from "../widgets/registry";

const def = (type: string) => WIDGETS[widgetType(type) ?? "chart"];

export type SaveResult<T extends LayoutItem = LayoutItem> =
  | { ok: true; version: number }
  | { ok: false; conflict: { layout: T[]; version: number; touched: string[]; by: string } };

export interface LayoutDeps<T extends LayoutItem = LayoutItem> {
  save: (layout: T[], baseVersion: number, touched: string[]) => Promise<SaveResult<T>>;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (h: unknown) => void;
}

export interface LayoutState<T extends LayoutItem = LayoutItem> {
  layout: T[];
  base: T[];                 // last version the server confirmed
  version: number;
  customizing: boolean;
  picked: string | null;        // keyboard: Enter picks up, Enter drops
  dragging: string | null;      // pointer drag in progress (preview layout lives in `layout`)
  touched: string[];            // widgets I moved/resized/added/edited since `base`
  past: T[][];
  future: T[][];
  status: "idle" | "saving" | "saved" | "merged" | "error";
  message: string;              // aria-live announcement
  /** The last 409 merge (90): who, the widgets both moved (mine kept; `theirs` is offered as Undo) and what they changed. */
  lastMerge: { by: string; conflicts: string[]; theirs: T[]; changedByThem: string[]; at: number } | null;

  setCustomizing: (on: boolean) => void;
  apply: (fn: (l: T[]) => T[], touched: string[], message?: string) => void;
  add: (type: string, at?: { x: number; y: number }) => string;
  remove: (id: string) => void;
  setConfig: (id: string, config: T["config"]) => void;
  // pointer drag: begin → preview(x,y) many times → drop / cancel
  beginDrag: (id: string) => void;
  preview: (x: number, y: number) => void;
  previewResize: (w: number, h: number) => void;
  /** Top/left corner handles: move and resize together. */
  previewBox: (box: Partial<Pick<LayoutItem, "x" | "y" | "w" | "h">>) => void;
  drop: (kind?: "move" | "resize") => void;
  cancelDrag: () => void;
  // keyboard
  pick: (id: string | null) => void;
  nudge: (dir: Dir, resize: boolean) => void;
  undo: () => void;
  redo: () => void;
  hydrate: (layout: T[], version: number) => void;
  /** Someone else's save arrived over the org channel while I'm not editing. */
  remote: (layout: T[], version: number) => void;
}

let seq = 0;
const newId = (type: string) => `${type}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export function createLayoutStore<T extends LayoutItem = LayoutItem>(initial: T[], version: number, deps: LayoutDeps<T>) {
  const schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
  const cancel = deps.cancel ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  let timer: unknown = null;
  let dragStart: T[] | null = null;

  return createStore<LayoutState<T>>()(subscribeWithSelector((set, get) => {
    const queueSave = () => {
      if (timer) cancel(timer);
      timer = schedule(async () => {
        timer = null;
        const { layout, version: v, touched } = get();
        set({ status: "saving" });
        try {
          const r = await deps.save(layout, v, touched);
          if (r.ok) { set({ status: "saved", version: r.version, base: layout, touched: [] }); return; }
          const m = mergeLayouts(get().base, get().layout, r.conflict.layout, { mine: get().touched, theirs: r.conflict.touched });
          set({ layout: m.layout, base: r.conflict.layout, version: r.conflict.version, status: "merged",
            lastMerge: { by: r.conflict.by, conflicts: m.conflicts.map((c) => c.id), theirs: m.conflicts.map((c) => c.theirs), changedByThem: m.changedByThem, at: Date.now() },
            message: `Merged ${r.conflict.by}’s changes${m.conflicts.length ? ` · ${m.conflicts.length} widget kept where you put it` : ""}` });
          queueSave();                                        // save the merged result on top of theirs
        } catch { set({ status: "error", message: "Couldn’t save the layout. Your changes are kept here." }); }
      }, recipes.autosave.debounceMs);
    };
    const commit = (next: T[], touched: string[], message = "") => {
      const prev = get().layout;
      set({ layout: next, status: "saving", past: [...get().past, prev].slice(-recipes.autosave.historySteps), future: [],
        touched: [...new Set([...get().touched, ...touched])], message });
      queueSave();
    };

    return {
      layout: initial, base: initial, version, customizing: false, picked: null, dragging: null, touched: [], past: [], future: [],
      status: "idle", message: "", lastMerge: null,

      setCustomizing: (customizing) => set({ customizing, picked: null }),
      apply: (fn, touched, message) => commit(fn(get().layout), touched, message),
      add: (type, at) => {
        const d = def(type);
        const id = newId(type);
        commit(addItem(get().layout, { id, type, w: d.defaultSize.w, h: d.defaultSize.h, config: {}, ...(at ?? {}) } as unknown as Omit<T, "x" | "y">), [id], `${d.title} added`);
        return id;
      },
      remove: (id) => {
        const it = get().layout.find((i) => i.id === id);
        commit(removeItem(get().layout, id), [id], it ? `${def(it.type).title} removed. Press Command Z to undo.` : "");
      },
      setConfig: (id, config) => commit(get().layout.map((i) => (i.id === id ? { ...i, config } : i)), [id], "Widget updated"),

      beginDrag: (id) => { dragStart = get().layout; set({ dragging: id }); },
      preview: (x, y) => { const id = get().dragging; if (id) set({ layout: moveItem(dragStart ?? get().layout, id, x, y) }); },
      previewResize: (w, h) => { const id = get().dragging; if (id) set({ layout: resizeItem(dragStart ?? get().layout, id, w, h) }); },
      previewBox: (box) => { const id = get().dragging; if (id) set({ layout: placeItem(dragStart ?? get().layout, id, box) }); },
      drop: (kind = "move") => {
        const id = get().dragging;
        if (!id || !dragStart) return;
        const settled = compact(get().layout);
        const it = settled.find((i) => i.id === id)!;
        const start = dragStart;
        dragStart = null;
        set({ layout: start, dragging: null });
        if (sameLayout(settled, start)) return;            // dropped where it started: no history step, no save
        commit(settled, [id], announce(it, kind));
      },
      cancelDrag: () => { if (dragStart) set({ layout: dragStart }); dragStart = null; set({ dragging: null, message: "Move cancelled" }); },

      pick: (picked) => {
        const it = get().layout.find((i) => i.id === picked);
        set({ picked, message: picked && it ? `${def(it.type).title} picked up. Arrows move, Shift and arrows resize, Enter drops, Escape cancels.` : "Dropped" });
      },
      nudge: (dir, resize) => {
        const id = get().picked;
        if (!id) return;
        const next = (resize ? keyboardResize : keyboardMove)(get().layout, id, dir);
        const it = next.find((i) => i.id === id)!;
        commit(next, [id], announce(it, resize ? "resize" : "move"));
      },
      undo: () => {
        const prev = get().past.at(-1);
        if (!prev) return;
        set({ layout: prev, past: get().past.slice(0, -1), future: [get().layout, ...get().future], message: "Undone" });
        queueSave();
      },
      redo: () => {
        const next = get().future[0];
        if (!next) return;
        set({ layout: next, future: get().future.slice(1), past: [...get().past, get().layout], message: "Redone" });
        queueSave();
      },
      hydrate: (layout, v) => set({ layout, base: layout, version: v, past: [], future: [], touched: [] }),
      remote: (layout, v) => {
        if (v <= get().version) return;
        if (!get().customizing && !get().touched.length) set({ layout, base: layout, version: v });
        // while editing, the next save gets a 409 and merges
      },
    };
  }));
}

export type LayoutStore<T extends LayoutItem = LayoutItem> = ReturnType<typeof createLayoutStore<T>>;
export const useLayout = <T extends LayoutItem, R>(store: LayoutStore<T>, sel: (s: LayoutState<T>) => R) => useStore(store, sel);
export type { LayoutItem };
